import React, { Fragment, useEffect, useRef, useState } from "react";
import Modal from "../Modal/Modal";
import UserProfile from "../UserProfile/UserProfile";
import { UserIcon } from "../icons/user";
import { useAuth } from "@/context/AuthContext";
import { useImagine } from "@/context/ImagineContext";
import { isEmptyObject } from "@/utils/object";
import { Logout, User } from "@carbon/icons-react";

const getInitials = (name?: string, email?: string): string => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return "U";
};

export const UserImage = (props: { size?: string }) => {
  const { user } = useAuth();

  const getImageStyles = () => {
    return {
      width: props.size,
      minWidth: props.size,
      maxWidth: props.size,
      height: props.size,
      minHeight: props.size,
      maxHeight: props.size,
    };
  };

  const isMsAccount = user.email && user.email.endsWith("@minecraft.local");
  const initials = getInitials(
    user.name || user.minecraft_username,
    user.email
  );

  const size = props.size || "40px";
  return (
    <div
      className="w-10 min-w-10 max-w-10 h-10 min-h-10 max-h-10 cursor-pointer rounded-full flex justify-center items-center"
      style={{
        width: size,
        height: size,
        minWidth: size,
        maxWidth: size,
        minHeight: size,
        maxHeight: size,
      }}
    >
      {user.avatar ? (
        <img
          src={user.avatar}
          alt="avatar"
          className="w-full h-full rounded-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full rounded-full bg-[#252238] border border-[#312E47] text-white flex items-center justify-center font-bold text-lg tracking-wide">
          {isMsAccount ? (
            initials
          ) : (
            <UserIcon
              width={props.size || "24px"}
              height={props.size || "24px"}
            />
          )}
        </div>
      )}
    </div>
  );
};

interface UserCardProps {
  showCard: boolean;
  setShowCard: (show: boolean) => void;
}

const UserCard = (props: UserCardProps) => {
  const { user, logout } = useAuth();
  const { clearResults } = useImagine();
  const [showEditModal, setShowEditModal] = useState(false);

  const handleLogout = () => {
    clearResults();
    if (user.email && user.email.endsWith("@minecraft.local")) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      window.location.href = "/welcome";
    } else {
      logout();
    }
  };

  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!props.showCard) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        props.setShowCard(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [props.showCard]);

  if (isEmptyObject(user)) return null;

  return (
    <Fragment>
      {props.showCard && (
        <div
          ref={cardRef}
          className="absolute top-12 right-0 z-50 bg-[#14172D] border border-[#312E47] rounded-xl p-4
          flex flex-col gap-4 min-w-[200px] shadow-lg"
        >
            <div
              className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[#252238] border border-[#312E47]
              text-white cursor-pointer transition-all duration-150 hover:bg-[#8FCA5C] hover:border-[#8FCA5C]
              hover:text-[#242C36] group"
              onClick={(e) => {
                e.stopPropagation();
                setShowEditModal(true);
              }}
            >
              <User size={18} />
              <span className="text-sm font-medium">Edit Profile</span>
            </div>

            <div
              className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[#252238] border border-[#312E47]
              text-white cursor-pointer transition-all duration-150 hover:bg-red-900/30
              hover:border-red-500 hover:text-red-400"
              onClick={(e) => {
                e.stopPropagation();
                handleLogout();
              }}
            >
              <Logout size={18} />
              <span className="text-sm font-medium">Log out</span>
            </div>
        </div>
      )}

      <Modal
        showHeader={true}
        headerContent={<h2 className="headline-4">Edit Profile</h2>}
        width={"auto"}
        height={"auto"}
        isOpen={showEditModal}
        toggleModal={() => setShowEditModal(!showEditModal)}
      >
        <UserProfile />
      </Modal>
    </Fragment>
  );
};

export default UserCard;
